import { useState } from 'react';
import AppIcon from '../../../components/AppIcon';

const TemplateEditor = ({ template, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: template?.name || '',
    subject: template?.subject || '',
    body: template?.body || '',
    description: template?.description || '',
    status: template?.status || 'active'
  });

  const variables = [
    { key: '{{customer_name}}', label: 'اسم العميل' },
    { key: '{{photographer_name}}', label: 'اسم المصور' },
    { key: '{{service_type}}', label: 'نوع الخدمة' },
    { key: '{{booking_date}}', label: 'تاريخ الحجز' },
    { key: '{{booking_time}}', label: 'وقت الحجز' },
    { key: '{{booking_id}}', label: 'رقم الحجز' },
    { key: '{{location}}', label: 'الموقع' },
    { key: '{{price}}', label: 'السعر' },
    { key: '{{amount}}', label: 'المبلغ' },
    { key: '{{transaction_id}}', label: 'رقم العملية' },
    { key: '{{payment_date}}', label: 'تاريخ الدفع' },
    { key: '{{due_date}}', label: 'تاريخ الاستحقاق' },
    { key: '{{new_status}}', label: 'الحالة الجديدة' }
  ];

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const insertVariable = (variable, field) => {
    const textarea = document.getElementById(field);
    const start = textarea?.selectionStart;
    const end = textarea?.selectionEnd;
    const text = formData?.[field];
    const newText = text?.substring(0, start) + variable + text?.substring(end);
    handleChange(field, newText);
    
    setTimeout(() => {
      textarea?.focus();
      textarea?.setSelectionRange(start + variable?.length, start + variable?.length);
    }, 0);
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    onSave({
      ...template,
      ...formData,
      lastModified: new Date()?.toISOString()?.split('T')?.[0]
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" dir="rtl">
      <div className="bg-card rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-foreground">تحرير القالب</h2>
            <p className="text-sm text-muted-foreground mt-1">تعديل محتوى قالب الإيميل</p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full hover:bg-muted flex items-center justify-center transition-colors"
          >
            <AppIcon name="X" size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Template Name */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-foreground mb-2">
              اسم القالب
            </label>
            <input
              type="text"
              value={formData?.name}
              onChange={(e) => handleChange('name', e?.target?.value)}
              className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              required
            />
          </div>

          {/* Description */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-foreground mb-2">
              الوصف
            </label>
            <input
              type="text"
              value={formData?.description}
              onChange={(e) => handleChange('description', e?.target?.value)}
              className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Status */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-foreground mb-2">
              الحالة
            </label>
            <select
              value={formData?.status}
              onChange={(e) => handleChange('status', e?.target?.value)}
              className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="active">نشط</option>
              <option value="inactive">غير نشط</option>
            </select>
          </div>

          {/* Subject */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-foreground">
                موضوع الإيميل
              </label>
              <div className="flex gap-1">
                {variables?.slice(0, 4)?.map(v => (
                  <button
                    key={v?.key}
                    type="button"
                    onClick={() => insertVariable(v?.key, 'subject')}
                    className="px-2 py-1 text-xs bg-primary/10 text-primary rounded hover:bg-primary/20 transition-colors"
                    title={v?.label}
                  >
                    {v?.label}
                  </button>
                ))}
              </div>
            </div>
            <input
              id="subject"
              type="text"
              value={formData?.subject}
              onChange={(e) => handleChange('subject', e?.target?.value)}
              className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              required
            />
          </div>

          {/* Body */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-foreground">
                محتوى الإيميل
              </label>
              <div className="text-xs text-muted-foreground">
                استخدم المتغيرات الديناميكية أدناه
              </div>
            </div>
            <textarea
              id="body"
              value={formData?.body}
              onChange={(e) => handleChange('body', e?.target?.value)}
              rows={12}
              className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary font-mono text-sm"
              required
            />
          </div>

          {/* Variables Panel */}
          <div className="mb-6 p-4 bg-muted/30 rounded-lg">
            <p className="text-sm font-medium text-foreground mb-3">المتغيرات الديناميكية المتاحة:</p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {variables?.map(v => (
                <button
                  key={v?.key}
                  type="button"
                  onClick={() => insertVariable(v?.key, 'body')}
                  className="flex items-center gap-2 px-3 py-2 bg-card border border-border rounded-lg hover:bg-primary/10 hover:border-primary transition-colors text-right"
                >
                  <AppIcon name="Plus" size={14} className="text-primary" />
                  <div>
                    <p className="text-xs font-medium text-foreground">{v?.label}</p>
                    <p className="text-xs text-muted-foreground">{v?.key}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-border rounded-lg hover:bg-muted transition-colors"
            >
              إلغاء
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
            >
              <AppIcon name="Save" size={18} />
              <span>حفظ التغييرات</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TemplateEditor;